import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Database, Table as TableIcon, FileText, ChevronRight, ChevronDown, ChevronUp,
  CheckSquare, Square, ArrowLeft, Loader2, Radio, Folder, File, FileCode,
  FileImage, FileVideo, FileAudio, Trash2, Brain, Search, AlertTriangle, X as XIcon,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import exploreApi from './exploreApi';
import LLMAnalysis from './components/explore/LLMAnalysis';
import DataChat from './components/explore/DataChat';
import AIDescriptions from './components/explore/AIDescriptions';
import BulkMetadataGenerator from './components/explore/BulkMetadataGenerator';

// ── File icon helper ──────────────────────────────────────────────────────────
function getFileIcon(node) {
  if (node.type === 'database') return Database;
  if (node.type === 'table') return TableIcon;
  if (node.type === 'topic') return Radio;
  if (node.type === 'schema') return Folder;
  if (node.type === 'view') return FileText;
  if (node.type === 'file') {
    const ext = node.file_extension?.toLowerCase() || '';
    if (['.js', '.ts', '.py', '.java', '.cpp', '.c', '.html', '.css', '.go', '.rs'].includes(ext)) return FileCode;
    if (['.jpg', '.jpeg', '.png', '.gif', '.svg', '.bmp'].includes(ext)) return FileImage;
    if (['.mp4', '.avi', '.mov', '.mkv'].includes(ext)) return FileVideo;
    if (['.mp3', '.wav', '.flac', '.aac'].includes(ext)) return FileAudio;
    return File;
  }
  return FileText;
}

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// ── Tree node component ───────────────────────────────────────────────────────
function TreeNode({ node, level, selectedPaths, expandedPaths, onToggleSelect, onToggleExpand, onSelectObject, onDeleteFile }) {
  const hasChildren = node.children?.length > 0;
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedPaths.has(node.path);
  const Icon = getFileIcon(node);
  const isLeaf = ['table', 'view', 'file'].includes(node.type);

  return (
    <div className="select-none">
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-blue-50 rounded-lg transition-colors group" style={{ marginLeft: `${level * 20}px` }}>
        {hasChildren ? (
          <button onClick={() => onToggleExpand(node.path)} className="p-1 hover:bg-blue-100 rounded transition-colors">
            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-600" /> : <ChevronRight className="h-4 w-4 text-gray-600" />}
          </button>
        ) : <div className="w-6" />}

        <button onClick={() => onToggleSelect(node.path)} className="p-0.5 hover:bg-blue-100 rounded transition-colors">
          {isSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4 text-gray-400" />}
        </button>

        <Icon className="h-4 w-4 text-primary flex-shrink-0" />

        <div className="flex-1 flex items-center gap-2 min-w-0">
          {isLeaf ? (
            <button onClick={() => onSelectObject(node.path, node.type, node.file_path)} className="text-sm font-medium whitespace-nowrap hover:text-primary hover:underline transition-colors text-left truncate" title={node.name}>
              {node.name}
            </button>
          ) : (
            <span className="text-sm font-medium whitespace-nowrap truncate" title={node.name}>{node.name}</span>
          )}
          {node.rowCount != null && <span className="text-xs text-gray-400 flex-shrink-0">({node.rowCount.toLocaleString()} rows)</span>}
          {node.size != null && <span className="text-xs text-gray-400 flex-shrink-0">({formatSize(node.size)})</span>}
          {node.hasPII && <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-[10px]">PII</span>}
        </div>

        {isLeaf && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={e => { e.stopPropagation(); onSelectObject(node.path, node.type, node.file_path); }} className="h-7 px-2 flex items-center rounded hover:bg-blue-100" title="Preview">
              <TableIcon className="h-3 w-3 text-gray-500" />
            </button>
            {node.type === 'file' && onDeleteFile && (
              <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete "${node.name}"?`)) onDeleteFile(node.path, node.name); }} className="h-7 px-2 flex items-center rounded hover:bg-red-50 hover:text-red-600" title="Delete">
                <Trash2 className="h-3 w-3 text-gray-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map(child => (
            <TreeNode key={child.path} node={child} level={level + 1} selectedPaths={selectedPaths} expandedPaths={expandedPaths} onToggleSelect={onToggleSelect} onToggleExpand={onToggleExpand} onSelectObject={onSelectObject} onDeleteFile={onDeleteFile} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const SELECTABLES = new Set(['table', 'view', 'file', 'collection', 'topic']);
const TABS = ['Data Preview', 'Schema', 'Synthetic Data', 'AI Analysis', 'AI Descriptions', 'AI Chat'];

export default function SourceExplorer() {
  const navigate = useNavigate();
  const location = useLocation();
  const sourceId = new URLSearchParams(location.search).get('id');

  const [source, setSource] = useState(null);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPaths, setSelectedPaths] = useState(new Set());
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [tablePreview, setTablePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeTab, setActiveTab] = useState('Data Preview');
  const [showColumnSchema, setShowColumnSchema] = useState(true);
  const [showSampleData, setShowSampleData] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [showObjectsPanel, setShowObjectsPanel] = useState(true);
  const [showBulkMetadata, setShowBulkMetadata] = useState(false);
  const [objectSearch, setObjectSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  // Synthetic data
  const [synthLoading, setSynthLoading] = useState(false);
  const [synthPreview, setSynthPreview] = useState(null);
  const [synthPage, setSynthPage] = useState(0);
  const SYNTH_PAGE_SIZE = 50;

  // ── Helpers ──
  const findNode = useCallback((nodes, targetPath) => {
    for (const n of nodes) {
      if (n.path === targetPath) return n;
      if (n.children?.length) { const found = findNode(n.children, targetPath); if (found) return found; }
    }
    return null;
  }, []);

  const collectAllPaths = (node) => {
    const paths = [node.path];
    if (node.children?.length) node.children.forEach(c => paths.push(...collectAllPaths(c)));
    return paths;
  };

  const collectAllTables = (nodes) => {
    const tables = [];
    const traverse = (n) => {
      if (['table', 'view'].includes(n.type)) tables.push({ path: n.path, name: n.name, type: n.type, rowCount: n.rowCount });
      n.children?.forEach(traverse);
    };
    nodes.forEach(traverse);
    return tables;
  };

  const selectedObjectCount = (() => {
    const leaves = new Set();
    const traverse = (nodes) => { for (const n of nodes) { if (SELECTABLES.has(n.type)) leaves.add(n.path); if (n.children) traverse(n.children); } };
    traverse(objects);
    let count = 0;
    for (const p of selectedPaths) if (leaves.has(p)) count++;
    return count;
  })();

  // ── Load source data ──
  const loadSourceData = useCallback(async () => {
    if (!sourceId) return;
    try {
      setLoading(true);
      const src = await exploreApi.getSource(sourceId);
      setSource(src);
      if (src.status === 'connected') {
        const objs = await exploreApi.getSourceObjects(sourceId);
        setObjects(objs);
      }
    } catch (err) {
      console.error('Failed to load source:', err);
    } finally {
      setLoading(false);
    }
  }, [sourceId]);

  useEffect(() => { loadSourceData(); }, [loadSourceData]);

  // ── Tree handlers ──
  const handleToggleSelect = (path) => {
    const next = new Set(selectedPaths);
    const node = findNode(objects, path);
    if (node?.children?.length) {
      const allPaths = collectAllPaths(node);
      if (next.has(path)) allPaths.forEach(p => next.delete(p));
      else allPaths.forEach(p => next.add(p));
    } else {
      next.has(path) ? next.delete(path) : next.add(path);
    }
    setSelectedPaths(next);
  };

  const handleToggleExpand = (path) => {
    const next = new Set(expandedPaths);
    next.has(path) ? next.delete(path) : next.add(path);
    setExpandedPaths(next);
  };

  const handleSelectObject = async (path, objectType, filePath) => {
    if (!sourceId || !['table', 'view', 'file'].includes(objectType)) return;
    setSelectedObject({ path, type: objectType, file_path: filePath });
    setSelectedSheet(null);
    setActiveTab('Data Preview');
    const next = new Set(selectedPaths);
    next.add(path);
    setSelectedPaths(next);
    try {
      setLoadingPreview(true);
      const preview = await exploreApi.getTablePreview(sourceId, path, 1000);
      setTablePreview(preview);
      if (preview.sheets?.length > 0) setSelectedSheet(preview.current_sheet || preview.sheets[0].name);
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSheetChange = async (sheetName) => {
    if (!sourceId || !selectedObject) return;
    setSelectedSheet(sheetName);
    try {
      setLoadingPreview(true);
      const preview = await exploreApi.getTablePreview(sourceId, selectedObject.path, 1000, sheetName);
      setTablePreview(preview);
    } catch { /* silent */ } finally {
      setLoadingPreview(false);
    }
  };

  const handleDeleteFile = async (filePath, fileName) => {
    try {
      await exploreApi.deleteFile(fileName);
      if (selectedObject?.path === filePath) { setSelectedObject(null); setTablePreview(null); }
      loadSourceData();
    } catch (err) {
      alert('Failed to delete file. Please try again.');
    }
  };

  // ── Synthetic data ──
  const generateSynthetic = async (rows = 100) => {
    if (!sourceId || !selectedObject) return;
    try {
      setSynthLoading(true);
      setSynthPreview(null);
      setSynthPage(0);
      const response = await exploreApi.generateSynthetic(sourceId, selectedObject.path, rows);
      const payload = response?.data ?? response ?? {};
      let columns = [];
      let dataRows = [];

      const extractPreview = (p) => {
        if (!p) return;
        if (Array.isArray(p.columns)) {
          columns = p.columns.map(c => (typeof c === 'object' ? c.name || String(c) : c));
        }
        const candidates = p.rows ?? p.sample_data ?? p.data ?? (Array.isArray(p) ? p : null);
        if (Array.isArray(candidates) && candidates.length > 0) {
          const first = candidates[0];
          if (Array.isArray(first)) {
            if (!columns.length) columns = first.map((_, i) => `col_${i}`);
            dataRows = candidates.map(r => Object.fromEntries(columns.map((c, i) => [c, r[i]])));
          } else if (first && typeof first === 'object') {
            dataRows = candidates;
            if (!columns.length) columns = Object.keys(first);
          }
        }
      };

      extractPreview(payload.data ?? payload);

      // Try downloadId fallback
      if (!dataRows.length && response?.outputFiles?.length) {
        const dlId = response.outputFiles[0].downloadId || response.outputFiles[0].name;
        if (dlId) {
          try {
            const p = await exploreApi.getSyntheticPreview(dlId, rows);
            extractPreview(p?.preview ?? p?.preview_data ?? p?.data ?? p);
          } catch { /* silent */ }
        }
      }

      if (!dataRows.length && tablePreview?.sample_data?.length) {
        columns = tablePreview.columns.map(c => c.name);
        dataRows = tablePreview.sample_data;
      }

      setSynthPreview({ columns, rows: dataRows });
    } catch (err) {
      console.error('Synthetic generation failed:', err);
      alert('Synthetic generation failed. Check backend logs.');
    } finally {
      setSynthLoading(false);
    }
  };

  // ── Tree filter ──
  const filterTree = (nodes) => {
    if (typeFilter === 'all') return nodes;
    return nodes.reduce((acc, node) => {
      if (node.type === typeFilter) { acc.push({ ...node, children: [] }); }
      else if (node.children) { const filtered = filterTree(node.children); if (filtered.length) acc.push({ ...node, children: filtered }); }
      return acc;
    }, []);
  };

  // ── Type filter counts ──
  const typeCounts = (() => {
    const relevant = new Set(['database', 'schema', 'table', 'view', 'collection', 'topic', 'file']);
    const counts = { all: 0 };
    const count = (nodes) => { for (const n of nodes) { if (relevant.has(n.type)) { counts[n.type] = (counts[n.type] || 0) + 1; counts.all++; } if (n.children) count(n.children); } };
    count(objects);
    return counts;
  })();

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
          <p className="text-gray-700">Loading source data…</p>
        </div>
      </div>
    );
  }

  if (!source) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
        <div className="text-center">
          <Database className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-4">Source not found</h3>
          <button onClick={() => navigate('/data-studio', { state: { tab: 'analyzeData' } })} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm mx-auto hover:bg-primaryDark">
            <ArrowLeft className="h-4 w-4" /> Back to Analyze
          </button>
        </div>
      </div>
    );
  }

  const filteredObjects = filterTree(objects);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/data-studio', { state: { tab: 'analyzeData' } })} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{source.name}</h1>
                <p className="text-sm text-gray-500 mt-0.5">{source.connector} · {selectedObjectCount} object{selectedObjectCount !== 1 ? 's' : ''} selected</p>
              </div>
            </div>
            <div className="flex gap-3">
              {source.connector !== 'file_upload' && collectAllTables(objects).length > 0 && (
                <button onClick={() => setShowBulkMetadata(s => !s)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium ${showBulkMetadata ? 'bg-primary text-white border-primary' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <Brain className="h-4 w-4" /> {showBulkMetadata ? 'Hide' : 'Show'} Metadata Generator
                </button>
              )}
              <button
                disabled={selectedObjectCount < 2}
                onClick={() => {
                  const paths = Array.from(selectedPaths).filter(p => { const n = findNode(objects, p); return n && SELECTABLES.has(n.type); });
                  navigate(`/domain-analysis?source=${sourceId}&paths=${encodeURIComponent(JSON.stringify(paths))}`);
                }}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Brain className="h-4 w-4" /> Analyze Domains ({selectedObjectCount})
              </button>
              <button
                disabled={selectedObjectCount === 0}
                onClick={() => {
                  const paths = Array.from(selectedPaths).filter(p => { const n = findNode(objects, p); return n && SELECTABLES.has(n.type); });
                  navigate('/data-studio', { state: { tab: 'ingestData', source: sourceId, paths } });
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue to Ingestion ({selectedObjectCount}) <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Bulk Metadata Generator */}
        {showBulkMetadata && source.connector !== 'file_upload' && sourceId && (
          <div className="mb-6">
            <BulkMetadataGenerator sourceId={sourceId} sourceName={source.name} tables={collectAllTables(objects)} onComplete={() => {}} />
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Objects panel */}
          {showObjectsPanel && (
            <div className="col-span-4">
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {source.connector === 'kafka' ? 'Kafka Topics' : source.connector === 'file_upload' ? 'Uploaded Files' : 'Database Objects'}
                      </p>
                      {selectedObjectCount > 0 && <p className="text-xs text-gray-500">{selectedObjectCount} selected</p>}
                    </div>
                    <button onClick={() => setShowObjectsPanel(false)} className="p-1 hover:bg-gray-100 rounded" title="Hide panel">
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input type="text" placeholder="Search tables, views, files…" value={objectSearch} onChange={e => setObjectSearch(e.target.value)} className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    {objectSearch && <button onClick={() => setObjectSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><XIcon className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <div className="p-4">
                  {/* Type filter */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {['all', ...Object.keys(typeCounts).filter(t => t !== 'all' && typeCounts[t] > 0)].map(t => (
                      <button key={t} onClick={() => setTypeFilter(t)} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${typeFilter === t ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}>
                        {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1) + 's'} ({typeCounts[t] || 0})
                      </button>
                    ))}
                  </div>

                  <div className="max-h-[500px] overflow-y-auto overflow-x-auto">
                    {filteredObjects.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Database className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">{source.connector === 'file_upload' ? 'Upload files to get started' : 'No objects found'}</p>
                      </div>
                    ) : objectSearch.trim() ? (() => {
                      const term = objectSearch.trim().toLowerCase();
                      const matches = [];
                      const flatten = (nodes) => { for (const n of nodes) { if (n.name.toLowerCase().includes(term)) matches.push(n); if (n.children) flatten(n.children); } };
                      flatten(filteredObjects);
                      return matches.length === 0 ? (
                        <p className="text-center py-8 text-gray-500 text-sm">No matches for "{objectSearch}"</p>
                      ) : matches.map(obj => (
                        <TreeNode key={obj.path} node={{ ...obj, children: [] }} level={0} selectedPaths={selectedPaths} expandedPaths={expandedPaths} onToggleSelect={handleToggleSelect} onToggleExpand={handleToggleExpand} onSelectObject={handleSelectObject} onDeleteFile={handleDeleteFile} />
                      ));
                    })() : filteredObjects.map(obj => (
                      <TreeNode key={obj.path} node={obj} level={0} selectedPaths={selectedPaths} expandedPaths={expandedPaths} onToggleSelect={handleToggleSelect} onToggleExpand={handleToggleExpand} onSelectObject={handleSelectObject} onDeleteFile={handleDeleteFile} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show panel button */}
          {!showObjectsPanel && (
            <div className="col-span-1">
              <button onClick={() => setShowObjectsPanel(true)} title="Show objects panel" className="p-1 hover:bg-gray-100 rounded">
                <ChevronRight className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          )}

          {/* Preview area */}
          <div className={showObjectsPanel ? 'col-span-8' : 'col-span-11'}>
            {loadingPreview ? (
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-gray-500">Loading preview…</p>
              </div>
            ) : selectedObject && tablePreview && !showBulkMetadata ? (
              <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg overflow-hidden">
                <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                  <p className="font-semibold text-gray-900">{selectedObject.type === 'file' ? 'File Preview' : 'Table Preview'}: {selectedObject.path}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{selectedObject.type === 'file' ? `${tablePreview.row_count || 0} lines` : `${tablePreview.row_count} of max ${tablePreview.limit} rows`}</p>
                </div>
                <div className="p-5">
                  {/* Excel sheets */}
                  {tablePreview.sheets?.length > 1 && (
                    <div className="mb-5 pb-5 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Sheets:</p>
                      <div className="flex gap-2 flex-wrap">
                        {tablePreview.sheets.map(sheet => (
                          <button key={sheet.name} onClick={() => handleSheetChange(sheet.name)} disabled={!sheet.has_data} className={`px-3 py-1 text-sm rounded-md border transition-colors ${selectedSheet === sheet.name ? 'bg-primary/10 text-primary border-primary/30' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}>
                            {sheet.name}{sheet.columns > 0 && <span className="ml-1 text-xs opacity-70">({sheet.columns} cols)</span>}
                            {sheet.error && <AlertTriangle className="ml-1 h-3 w-3 text-red-500 inline" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tab bar */}
                  <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto whitespace-nowrap">
                    {TABS.map(tab => (
                      <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px shrink-0 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Data Preview */}
                  {activeTab === 'Data Preview' && (
                    <div>
                      <button onClick={() => setShowSampleData(s => !s)} className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border mb-3 transition-colors">
                        <span className="text-sm font-semibold text-gray-900">Sample Data ({tablePreview.row_count} rows)</span>
                        {showSampleData ? <ChevronUp className="h-5 w-5 text-gray-600" /> : <ChevronDown className="h-5 w-5 text-gray-600" />}
                      </button>
                      {showSampleData && (
                        <div className="overflow-x-auto rounded-lg border max-h-96">
                          <table className="w-full border-collapse">
                            <thead className="sticky top-0 bg-primary text-white">
                              <tr>{tablePreview.columns.map(col => <th key={col.name} className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">{col.name}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {tablePreview.sample_data.map((row, i) => (
                                <tr key={i} className="hover:bg-blue-50 transition-colors">
                                  {tablePreview.columns.map(col => (
                                    <td key={col.name} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap max-w-xs overflow-hidden text-ellipsis">
                                      {row[col.name] != null ? String(row[col.name]) : <span className="text-gray-400 italic">null</span>}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Schema */}
                  {activeTab === 'Schema' && (
                    <div>
                      <div className="mb-3 p-3 rounded-lg border bg-gray-50">
                        <p className="text-sm font-semibold text-gray-900">Column Schema ({tablePreview.columns.length} columns)</p>
                      </div>
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full border-collapse">
                          <thead className="bg-primary text-white">
                            <tr>
                              {['Column', 'Data Type', 'Nullable', 'Max Length', 'Precision', 'Statistics'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {tablePreview.columns.map(col => (
                              <tr key={col.name} className="hover:bg-blue-50 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {col.name}
                                  {col.patterns?.length > 0 && <div className="mt-1 flex gap-1">{col.patterns.map(p => <span key={p} className="px-1.5 py-0.5 border border-gray-300 rounded text-[10px] text-gray-600">{p}</span>)}</div>}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="px-2 py-0.5 border border-gray-300 rounded text-xs">{col.data_type}</span>
                                  {col.suggested_type && col.suggested_type !== col.data_type && <span className="ml-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-500">{col.suggested_type}</span>}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs ${col.nullable ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'}`}>{col.nullable ? 'Yes' : 'No'}</span>
                                  {typeof col.null_count === 'number' && <div className="text-xs text-gray-400 mt-1">{col.null_count} nulls</div>}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {col.max_length || '-'}
                                  {col.avg_length && <div className="text-xs text-gray-400">avg: {col.avg_length}</div>}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {col.precision ? `${col.precision}${col.scale ? `,${col.scale}` : ''}` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {col.unique_values && <div className="text-xs"><div>{col.unique_values} unique</div>{col.uniqueness_ratio && <div className="text-gray-400">{(col.uniqueness_ratio * 100).toFixed(1)}%</div>}</div>}
                                  {col.min_value != null && col.max_value != null && <div className="text-xs">Range: {col.min_value} – {col.max_value}</div>}
                                  {col.true_count != null && <div className="text-xs">T:{col.true_count} F:{col.false_count}</div>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Synthetic Data */}
                  {activeTab === 'Synthetic Data' && (
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Synthetic Data</p>
                      <p className="text-xs text-gray-500 mb-4">Generate a synthetic sample using the Synthetic Data agent.</p>
                      <div className="flex items-center gap-2 mb-4">
                        <input id="synthRows" type="number" defaultValue={100} min={1} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-24" />
                        <button onClick={() => generateSynthetic(Number(document.getElementById('synthRows').value) || 100)} disabled={synthLoading} className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primaryDark disabled:opacity-50">
                          {synthLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Generate
                        </button>
                      </div>
                      {synthPreview && (() => {
                        const total = synthPreview.rows.length;
                        const totalPages = Math.ceil(total / SYNTH_PAGE_SIZE);
                        const pageRows = synthPreview.rows.slice(synthPage * SYNTH_PAGE_SIZE, (synthPage + 1) * SYNTH_PAGE_SIZE);
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Rows {synthPage * SYNTH_PAGE_SIZE + 1}–{Math.min((synthPage + 1) * SYNTH_PAGE_SIZE, total)} of {total.toLocaleString()}</span>
                              <div className="flex items-center gap-1">
                                {[['«', () => setSynthPage(0), synthPage === 0], ['‹', () => setSynthPage(p => p - 1), synthPage === 0], [`${synthPage + 1}/${totalPages}`, null, true], ['›', () => setSynthPage(p => p + 1), synthPage >= totalPages - 1], ['»', () => setSynthPage(totalPages - 1), synthPage >= totalPages - 1]].map(([label, handler, disabled], i) => (
                                  handler ? <button key={i} onClick={handler} disabled={disabled} className="px-2 py-0.5 border border-gray-300 rounded text-xs disabled:opacity-40">{label}</button> : <span key={i} className="px-2 text-xs text-gray-500">{label}</span>
                                ))}
                              </div>
                            </div>
                            <div className="overflow-auto rounded-lg border max-h-80">
                              <table className="w-full">
                                <thead className="bg-primary text-white sticky top-0">
                                  <tr>{synthPreview.columns.map(c => <th key={c} className="px-3 py-2 text-left text-xs font-bold">{c}</th>)}</tr>
                                </thead>
                                <tbody>
                                  {pageRows.map((r, i) => (
                                    <tr key={i} className="hover:bg-blue-50 transition-colors">
                                      {synthPreview.columns.map(c => <td key={c} className="px-3 py-2 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{String(r[c] ?? '')}</td>)}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* AI Analysis */}
                  {activeTab === 'AI Analysis' && (
                    <LLMAnalysis
                      filePath={selectedObject.type === 'file' ? (selectedObject.file_path || selectedObject.path) : undefined}
                      sourceId={selectedObject.type !== 'file' ? sourceId : undefined}
                      objectPath={selectedObject.type !== 'file' ? selectedObject.path : undefined}
                      sheetName={selectedSheet || undefined}
                    />
                  )}

                  {/* AI Descriptions */}
                  {activeTab === 'AI Descriptions' && (
                    <AIDescriptions
                      filePath={selectedObject.type === 'file' ? (selectedObject.file_path || selectedObject.path) : undefined}
                      sourceId={selectedObject.type !== 'file' ? sourceId : undefined}
                      objectPath={selectedObject.type !== 'file' ? selectedObject.path : undefined}
                      sheetName={selectedSheet || undefined}
                    />
                  )}

                  {/* AI Chat */}
                  {activeTab === 'AI Chat' && (
                    <DataChat
                      filePath={selectedObject.type === 'file' ? (selectedObject.file_path || selectedObject.path) : undefined}
                      sourceId={selectedObject.type !== 'file' ? sourceId : undefined}
                      objectPath={selectedObject.type !== 'file' ? selectedObject.path : undefined}
                      sheetName={selectedSheet || undefined}
                      tablePreview={tablePreview}
                    />
                  )}
                </div>
              </div>
            ) : !showBulkMetadata ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 shadow-lg p-12 text-center">
                <TableIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Table or View</h3>
                <p className="text-gray-500 text-sm">Click any table or view in the panel to see its structure and sample data</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
